import React, { memo, useCallback } from 'react';
// eslint-disable-next-line no-unused-vars
import { motion, AnimatePresence } from 'framer-motion';
import useNavigationStore from '../../store/navigationStore';
import {
    Store, MonitorPlay, Sparkles, PieChart, Users, Smartphone,
    ChevronLeft, ArrowRight, Package, ChefHat, HandCoins,
    Eye, PenTool, Gift, Printer, Building2, Award, LayoutGrid
} from 'lucide-react';
import { listenFeatureFlags } from '../../services/featureFlagsService';

/**
 * Hub definitions based on the user request and existing menu items.
 */
const HUBS = [
    {
        id: 'finance_hub',
        label: 'Tài chính (Dữ liệu)',
        description: 'Doanh thu & Lãi lỗ',
        icon: PieChart,
        color: 'from-slate-600 to-slate-800',
        items: ['financial'],
        permissions: ['finance.read']
    },
    {
        id: 'system_hub',
        label: 'Hệ thống & Hồ sơ',
        description: 'Cài đặt quán & Chi nhánh',
        icon: Building2,
        color: 'from-slate-500 to-slate-700',
        items: ['brand', 'settings'],
    },
    {
        id: 'pos_hub',
        label: 'Bán hàng (POS)',
        description: 'Vào ca & Bán hàng nhanh',
        icon: Store,
        color: 'from-orange-500 to-red-600',
        items: ['pos'],
        permissions: ['pos.access']
    },
    {
        id: 'ops_hub',
        label: 'Vận hành & Hậu cần',
        description: 'Bếp, Kho & Nhà cung cấp',
        icon: MonitorPlay,
        color: 'from-emerald-500 to-teal-600',
        items: ['pos_monitor', 'kds', 'inventory', 'suppliers', 'central_inventory'],
    },
    {
        id: 'growth_hub',
        label: 'Tăng trưởng & Sáng tạo',
        description: 'Marketing, Menu & Trends',
        icon: Sparkles,
        color: 'from-indigo-500 to-purple-600',
        items: ['trends', 'competitor', 'content', 'promo_factory', 'menu_generator'],
        permissions: ['menu.read', 'menu.write']
    },
    {
        id: 'team_crm_hub',
        label: 'Đội ngũ & Khách hàng',
        description: 'Nhân sự & CRM Hội viên',
        icon: Users,
        color: 'from-blue-500 to-indigo-600',
        items: ['staff_management', 'hrm', 'staff_portal', 'crm'],
    }
];

/**
 * Detailed menu items mapping (synced with Sidebar.jsx logic but for Grid)
 */
const FEATURE_ITEMS = {
    pos: { label: 'Máy bán hàng', icon: Smartphone, description: 'Giao diện POS', color: 'orange', permissions: ['pos.access'] },
    pos_monitor: { label: 'Giám sát POS', icon: MonitorPlay, description: 'Theo dõi thời gian thực', color: 'rose', permissions: ['pos.access', 'pos.manage'] },
    kds: { label: 'Màn hình Bếp', icon: ChefHat, description: 'Điều phối chế biến', color: 'orange', permissions: ['pos.access'] },
    inventory: { label: 'Kho & Định mức', icon: Package, description: 'Quản lý tồn kho', color: 'blue', permissions: ['inventory.read', 'inventory.write'] },
    suppliers: { label: 'Công nợ NCC', icon: HandCoins, description: 'Nhà cung cấp', color: 'amber', permissions: ['inventory.read', 'inventory.write'] },
    central_inventory: { label: 'Luân chuyển', icon: Package, description: 'Kho tổng center', color: 'indigo', permissions: ['inventory.read', 'inventory.write'] },

    trends: { label: 'Săn Trend', icon: Sparkles, description: 'Xu hướng thị trường', color: 'indigo', permissions: ['menu.read'] },
    competitor: { label: 'Soi đối thủ', icon: Eye, description: 'Phân tích cạnh tranh', color: 'rose', permissions: ['menu.read'] },
    content: { label: 'Xưởng thiết kế', icon: PenTool, description: 'Nội dung sáng tạo', color: 'purple', permissions: ['menu.write'] },
    promo_factory: { label: 'Khuyến mãi', icon: Gift, description: 'Chiến dịch ưu đãi', color: 'pink', permissions: ['menu.write'] },
    menu_generator: { label: 'In Menu', icon: Printer, description: 'Tạo thực đơn đẹp', color: 'orange', permissions: ['menu.read'] },

    financial: { label: 'Doanh thu', icon: PieChart, description: 'Tài chính & Lãi lỗ', color: 'emerald', permissions: ['finance.read'] },
    hrm: { label: 'Bảng lương', icon: HandCoins, description: 'Chấm công & Lương', color: 'blue', permissions: ['hrm.read', 'hrm.write'] },
    staff_management: { label: 'Điều phối NV', icon: Users, description: 'Phân quyền chi nhánh', color: 'indigo', permissions: ['hrm.write'] },
    staff_portal: { label: 'Cổng NV', icon: Smartphone, description: 'Dành cho nhân viên', color: 'teal' }, // Always
    brand: { label: 'Hồ sơ quán', icon: Store, description: 'Thông tin & Menu gốc', color: 'amber', permissions: ['menu.read', 'menu.write'] },
    settings: { label: 'Chi nhánh', icon: Building2, description: 'Quản lý địa điểm', color: 'slate' }, // Always
    crm: { label: 'Khách hàng', icon: Award, description: 'CRM & Hội viên', color: 'indigo', permissions: ['hrm.read'] },
};

const FeatureHubColorMap = {
    rose: 'from-rose-500 to-rose-600',
    orange: 'from-orange-500 to-orange-600',
    amber: 'from-amber-500 to-amber-600',
    blue: 'from-blue-500 to-blue-600',
    indigo: 'from-indigo-500 to-indigo-600',
    teal: 'from-teal-500 to-teal-600',
    emerald: 'from-emerald-500 to-emerald-600',
    slate: 'from-slate-500 to-slate-600',
    purple: 'from-purple-500 to-purple-600',
    pink: 'from-pink-500 to-pink-600'
};

const GridLauncher = ({ user, isOwner: isOwnerProp, managerPermissions: managerPermsProp }) => {
    const { navigateTo } = useNavigationStore();
    const [currentHub, setCurrentHub] = React.useState(null);
    const [flags, setFlags] = React.useState({});

    // Listen to flags
    React.useEffect(() => {
        if (!user?.uid) return;
        return listenFeatureFlags(user.uid, null, setFlags);
    }, [user?.uid]);

    const handleHubClick = useCallback((hub) => {
        if (hub.isExternal) {
            window.open(hub.path, '_blank');
            return;
        }
        setCurrentHub(hub);
    }, []);

    const handleBack = useCallback(() => {
        setCurrentHub(null);
    }, []);

    const handleFeatureClick = useCallback((featureId) => {
        navigateTo(featureId);
    }, [navigateTo]);

    const isOwner = (isOwnerProp === true) || user?.role === 'owner' || window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    
    // Logic to check if a hub should be visible/active
    const isHubActive = (hub) => {
        if (isOwner) return true; // Owners see everything for upsell or config
        
        // Map hub items to flags
        const flagMap = {
            inventory: 'proInventoryEnabled',
            hrm: 'proHrEnabled',
            financial: 'proFinanceEnabled',
            trends: 'proMarketingEnabled'
        };

        return hub.items.every(item => {
            const flag = flagMap[item];
            return !flag || flags[flag];
        });
    };
    const managerPerms = managerPermsProp ?? (user?.managerPermissions || []);

    const hasPermission = (item) => {
        if (!item) return false;
        if (isOwner) return true;
        if (!item.permissions) return true; // Default visible like settings/staff_portal
        return item.permissions.some(p => managerPerms.includes(p));
    };

    const filteredHubs = HUBS.map(hub => {
        const filteredItems = hub.items?.filter(itemKey => hasPermission(FEATURE_ITEMS[itemKey])) || [];
        return { ...hub, items: filteredItems };
    }).filter(hub => {
        if (!isHubActive(hub)) return false; // Tiering check
        if (isOwner) return true;
        if (hub.id === 'system_hub') return true; // profile/settings always visible
        if (hub.id === 'pos_hub' && hasPermission(hub)) return true;
        if (hub.items && hub.items.length > 0) return true;
        return false;
    });

    // Framer motion variants
    const containerVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.4,
                staggerChildren: 0.1
            }
        },
        exit: { opacity: 0, scale: 0.95, transition: { duration: 0.2 } }
    };

    const itemVariants = {
        hidden: { opacity: 0, scale: 0.9 },
        visible: { opacity: 1, scale: 1 }
    };

    return (
        <div className="w-full max-w-4xl mx-auto px-4 py-6">
            <AnimatePresence mode="wait">
                {!currentHub ? (
                    <motion.div
                        key="home-grid"
                        variants={containerVariants}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6"
                    >
                        {filteredHubs.map((hub) => (
                            <motion.button
                                key={hub.id}
                                variants={itemVariants}
                                whileHover={{ scale: 1.02, y: -5 }}
                                whileTap={{ scale: 0.98 }}
                                onClick={() => handleHubClick(hub)}
                                className="relative overflow-hidden group entrance-glass p-8 rounded-[38px] shadow-[0_8px_32px_rgba(0,0,0,0.3)] hover:shadow-[0_12px_48px_rgba(99,102,241,0.25)] transition-all duration-500 text-left flex flex-col h-full border border-white/10 hover:border-white/20"
                            >
                                <div className="flex justify-between items-start mb-6 z-10 relative">
                                    <div className={`w-16 h-16 rounded-[24px] flex items-center justify-center bg-gradient-to-br ${hub.color} shadow-lg shadow-indigo-500/20`}>
                                        <hub.icon className="text-white" size={32} strokeWidth={2.5} />
                                    </div>
                                    <div className="bg-white/5 backdrop-blur-sm px-3 py-1.5 rounded-full border border-white/10 group-hover:bg-white/10 transition-colors">
                                        <ArrowRight className="text-slate-300 group-hover:text-indigo-400 group-hover:translate-x-1 transition-all" size={20} />
                                    </div>
                                </div>

                                <div className="space-y-1 mb-6 z-10 relative">
                                    <h3 className="text-2xl font-black text-white tracking-tight">{hub.label}</h3>
                                    <p className="text-slate-300 text-sm font-semibold leading-relaxed">{hub.description}</p>
                                </div>

                                {/* Interactive Widget / Quick Stats */}
                                <div className="mt-auto pt-6 border-t border-slate-700/50 flex gap-4 z-10 relative">
                                    <div className="flex-1">
                                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Trạng thái</p>
                                        <div className="flex items-center gap-2">
                                            <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                                            <span className="text-xs font-bold text-slate-300 group-hover:text-emerald-400 transition-colors">Ổn định</span>
                                        </div>
                                    </div>
                                    <div className="flex-1">
                                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Nổi bật</p>
                                        <span className="text-xs font-black text-indigo-400">
                                            {hub.id === 'finance_hub' ? '+12.5% moM' :
                                                hub.id === 'pos_hub' ? '4 Ca mở' :
                                                    hub.id === 'growth_hub' ? '2 Trends mới' :
                                                        hub.id === 'ops_hub' ? '8 Đơn hàng' : 'Cập nhật'}
                                        </span>
                                    </div>
                                </div>

                                {/* Abstract background glow */}
                                <div className={`absolute -right-12 -top-12 w-32 h-32 bg-gradient-to-br ${hub.color} opacity-0 group-hover:opacity-10 blur-3xl transition-opacity`} />
                            </motion.button>
                        ))}
                    </motion.div>
                ) : (
                    <motion.div
                        key="sub-grid"
                        variants={containerVariants}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                    >
                        <div className="flex items-center gap-4 mb-8">
                            <button
                                onClick={handleBack}
                                className="p-3 bg-slate-800/50 backdrop-blur-md border border-slate-700/50 rounded-2xl text-slate-300 hover:bg-slate-700 hover:text-white transition-all shadow-sm"
                            >
                                <ChevronLeft size={24} />
                            </button>
                            <div>
                                <h2 className="text-2xl font-bold text-white">{currentHub.label}</h2>
                                <p className="text-slate-400 font-medium">{currentHub.description}</p>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                            {currentHub.items.map((itemKey) => {
                                const item = FEATURE_ITEMS[itemKey];
                                if (!item) return null;
                                return (
                                    <motion.button
                                        key={itemKey}
                                        variants={itemVariants}
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                        onClick={() => handleFeatureClick(itemKey)}
                                        className="flex flex-col items-center entrance-glass p-5 rounded-[32px] hover:shadow-[0_4px_20px_rgba(99,102,241,0.15)] transition-all text-center border-white/5"
                                    >
                                        <div className={`w-12 h-12 rounded-2xl flex items-center justify-center mb-3 bg-gradient-to-br ${FeatureHubColorMap[item.color] || 'from-indigo-500 to-indigo-600'}`}>
                                            <item.icon className="text-white" size={24} />
                                        </div>
                                        <span className="text-sm font-bold text-white line-clamp-1">{item.label}</span>
                                        <span className="text-[10px] text-slate-400 font-medium mt-1 line-clamp-1">{item.description}</span>
                                    </motion.button>
                                );
                            })}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default memo(GridLauncher);
